import { bisector } from 'd3-array';
import { Delaunay } from 'd3-delaunay';
import { line } from 'd3-shape';
import type { Selection } from 'd3-selection';
import { pointer, select } from 'd3-selection';
import { first, flatMap, flatten, isEmpty, property } from 'lodash-es';
import sortBy from 'lodash-es/sortBy';

import type { Observable } from 'rxjs';
import {
    BehaviorSubject,
    combineLatest,
    EMPTY,
    map,
    of,
    ReplaySubject,
    switchMap,
    tap,
} from 'rxjs';
import type { LxEq, Lxn, LxPeak, LXtMax, LXtMin, VperBucket } from '@/graphql/types';
import { NotImplementedError } from '../utils/errors';
import type { GraphElementPosition, TooltipPoint } from './graph-elements/base-provider';
import { BaseGraphElementsProvider } from './graph-elements/base-provider';
import { shareReplayRefCount } from '../utils/rxjs';
import { EventGraphElement } from './graph-elements/events-provider';
import type { Graph, SensorDataSetGraph } from './graph';
import type { DataSet } from './dataset';

type TooltipElementRecord = Lxn | LxPeak | LxEq | LXtMax | LXtMin;

interface TooltipData {
    element: {
        getToolTipText(dataSet: DataSet, graph: SensorDataSetGraph): string;
        value: number;
        record: TooltipElementRecord;
    };
    getToolTipPoints(): TooltipPoint[];
    getToolTipCircles$(positions$: Observable<GraphElementPosition[]>): Observable<
        Array<{
            type: string;
            transform: string;
        }>
    >;
}

function positionsToPoints(positions: GraphElementPosition[]): TooltipPoint[] {
    return flatMap(positions, (record) => record.getToolTipPoints());
}

export class BaseTooltipHelper {
    graph: Graph;

    element: Selection<SVGGElement, unknown, null, undefined>;

    width: number;

    height: number;

    dataSet: DataSet;

    plot: Selection<SVGRectElement, unknown, null, undefined>;

    tooltip: Selection<HTMLDivElement, unknown, null, undefined>;

    event$: ReplaySubject<MouseEvent>;

    mouse$: Observable<[number, number]>;

    isMouseInGraph$: BehaviorSubject<boolean>;

    data$: Observable<TooltipData | null>;

    positions$: Observable<GraphElementPosition[]>;

    onlyShowToolTipForEvents$: Observable<boolean>;

    constructor(
        graph: Graph,
        element: Selection<SVGGElement, unknown, null, undefined>,
        width: number,
        height: number
    ) {
        this.graph = graph;
        this.element = element;
        this.width = width;
        this.height = height;

        this.dataSet = (graph as SensorDataSetGraph).data;

        this.plot = element
            .append('rect')
            .attr('class', 'tooltipHome')
            .attr('data-testid', 'tooltip-home')
            .attr('width', width)
            .attr('height', height);

        this.tooltip = select<HTMLDivElement, unknown>('body')
            .append<HTMLDivElement>('div')
            .attr('class', 'graphTooltip hide')
            .attr('data-testid', 'tooltip-balloon') as unknown as Selection<
            HTMLDivElement,
            unknown,
            null,
            undefined
        >;

        void this.graph.unmountPromise.then(() => {
            // Cleanup the tooltip balloon we attached to the document body.
            this.tooltip.remove();
        });

        this.event$ = new ReplaySubject<MouseEvent>(1);

        this.mouse$ = this.event$.pipe(map((event) => pointer(event, this.plot.node())));

        this.isMouseInGraph$ = new BehaviorSubject<boolean>(false);

        this.data$ = this.isMouseInGraph$.pipe(
            switchMap((mouseInGraph) =>
                mouseInGraph ? this.getDataForMouseLocation$() : of(null)
            ),
            shareReplayRefCount()
        );

        this.plot
            .on('mouseover', () => this.isMouseInGraph$.next(true))
            .on('mouseout', () => this.isMouseInGraph$.next(false))
            .on('mousemove', (event: MouseEvent) => this.event$.next(event))
            .on('click', () => {
                const traceUrls = this.tooltip.select<HTMLAnchorElement>('a');

                if (!traceUrls.empty()) {
                    window.open(traceUrls.node()!.href, '_blank');
                }
            });

        this.positions$ = (this.graph as SensorDataSetGraph).activePlugins$.pipe(
            map((providers) =>
                providers
                    // Collect all the plugins that provide element positions.
                    .filter(
                        (plugin): plugin is BaseGraphElementsProvider =>
                            plugin instanceof BaseGraphElementsProvider
                    )
                    // Map those plugin into an array of `graphElementPositions$`'s.
                    .map((provider) => provider.graphElementPositions$)
            ),
            switchMap((positionObservables) => {
                // Is there are no `graphElementPositions$`'s, default to an empty
                // array of positions.
                if (isEmpty(positionObservables)) {
                    return of([]);
                }

                // If there are `graphElementPositions$`'s, then start listening to
                // them all and combine them into a single array.
                return combineLatest(positionObservables).pipe(
                    map((positionArrays) => flatten(positionArrays))
                );
            }),
            shareReplayRefCount()
        );

        this.onlyShowToolTipForEvents$ = this.dataSet.dataTypeSettings$.pipe(
            map((x) => x.onlyShowToolTipForEvents ?? false)
        );

        this.initToolTipLineRenderer();
        this.initToolTipBalloonRenderer();
        this.initToolTipCirclesRenderer();
    }

    getDataForMouseLocation$(): Observable<TooltipData | null> {
        throw new NotImplementedError();
    }

    getVperPeriodForTimestamp(timestamp: number): VperBucket | undefined {
        return this.dataSet.vperPeriods.find((period) => {
            const { timespan } = period;

            return timespan!.from <= timestamp && timestamp <= timespan!.to;
        });
    }

    initToolTipCirclesRenderer(): void {
        const circles$ = this.data$.pipe(
            switchMap((data) => (data ? data.getToolTipCircles$(this.positions$) : of([])))
        );

        circles$
            .pipe(
                tap((circles) => {
                    this.element
                        .selectAll<SVGCircleElement, { type: string; transform: string }>(
                            '.tooltip-circle'
                        )
                        .data(circles)
                        .join('circle')
                        .attr('class', (d) => `tooltip-circle ${d.type}`)
                        .attr('r', 4)
                        .attr('transform', (d) => d.transform);
                })
            )
            .subscribe();
    }

    initToolTipLineRenderer(): void {
        const linesFromEventAndScale$ = combineLatest([
            this.event$,
            this.graph.yAxis.scaleUpdated$,
        ]).pipe(
            map(([event]) => {
                const { scale } = this.graph.yAxis;
                const domain = scale.domain();
                const plotRect = this.plot.node()!.getBoundingClientRect();
                const x = event.pageX - plotRect.left;

                return [
                    // The tooltip line:
                    [
                        // Start point:
                        [x, scale(domain[0])],
                        // End point:
                        [x, scale(domain[1])],
                    ],
                ];
            })
        );

        const drawLine = line<[number, number]>();

        const followMouseWithLine$ = this.dataSet.dataTypeSettings$.pipe(
            map((x) => x.followMouseWithLine ?? false)
        );

        const isActive$ = followMouseWithLine$.pipe(
            switchMap((followMouseWithLine) =>
                followMouseWithLine ? this.isMouseInGraph$ : of(false)
            )
        );

        isActive$
            .pipe(
                switchMap((isActive) =>
                    isActive
                        ? // When we are active, start listening to the
                          // `linesFromEventAndScale` observable.
                          linesFromEventAndScale$
                        : // When we are inactive we will return an empty
                          // array of 'lines'. This will make D3 remove the line.
                          of([])
                ),
                tap((lines) => {
                    this.element
                        .selectAll<SVGPathElement, [[number, number], [number, number]]>(
                            '.tooltip-line'
                        )
                        .data(lines as Array<[[number, number], [number, number]]>)
                        .join('svg:path')
                        .attr('class', 'tooltip-line')
                        .attr('d', drawLine as unknown as string);
                })
            )
            .subscribe();
    }

    getTooltipText(data: TooltipData): string {
        return data.element.getToolTipText(this.dataSet, this.graph as SensorDataSetGraph);
    }

    initToolTipBalloonRenderer(): void {
        const data$ = this.onlyShowToolTipForEvents$.pipe(
            switchMap((onlyShowToolTipForEvents) =>
                onlyShowToolTipForEvents
                    ? this.data$.pipe(
                          map((data) => (data?.element instanceof EventGraphElement ? data : null))
                      )
                    : this.data$
            )
        );

        data$
            .pipe(
                switchMap((data) => {
                    // When there is no data, hide the tooltip ans stop this stream.
                    if (!data) {
                        this.tooltip.classed('hide', true);
                        return EMPTY;
                    }

                    // When there is data, update the text and also start listening to the event$.
                    const tooltipText = this.getTooltipText(data);
                    this.tooltip.html(tooltipText);
                    return combineLatest([of(data), this.event$]);
                }),
                tap(([data, event]) => {
                    const { x } = first(data.getToolTipPoints())!;

                    // Contains the left offset of .tooltipHome relative to the document.
                    const tooltipHomeOffset =
                        this.plot.node()!.getBoundingClientRect().left + window.pageXOffset;

                    const offsetLeft = tooltipHomeOffset + x + 5;

                    this.tooltip
                        .style('top', `${event.pageY - 8 - 13 * 7}px`)
                        .style('left', `${offsetLeft}px`)
                        .classed('hide', false);
                })
            )
            .subscribe();
    }
}

/**
 * Tooltip helper that finds the best tooltip data to be shown
 * for the current mouse location trough a bisect on the x axis.
 */
export class BisectTooltipHelper extends BaseTooltipHelper {
    propertyX: (point: TooltipPoint) => number;

    constructor(
        graph: Graph,
        element: Selection<SVGGElement, unknown, null, undefined>,
        width: number,
        height: number
    ) {
        super(graph, element, width, height);

        this.propertyX = property<TooltipPoint, number>(this.getBisectPropertyNameX());
    }

    getBisectPropertyNameX(): 'x' | 'y' {
        throw new NotImplementedError();
    }

    bisectX(positions: TooltipPoint[], mouse: number): number {
        // eslint-disable-next-line @typescript-eslint/unbound-method
        const bisectorX = bisector((point: TooltipPoint) => this.propertyX(point)).left;
        return bisectorX(positions, mouse);
    }

    getDataForMouseLocation$(): Observable<TooltipData | null> {
        return combineLatest([this.mouse$, this.positions$]).pipe(
            map(([mouse, positions]) => {
                const mouseX = mouse[0];

                const points = sortBy(positionsToPoints(positions), 'x');

                // Convert the mouse position to data positions.
                const i = this.bisectX(points, mouseX);
                const recordBeforeMouse = points[i - 1];
                const recordAfterMouse = points[i];
                let d: TooltipPoint | null = null;

                if (recordBeforeMouse && recordAfterMouse) {
                    const diffBeforeMouse = mouseX - this.propertyX(recordBeforeMouse);
                    const diffAfterMouse = this.propertyX(recordAfterMouse) - mouseX;
                    d = diffBeforeMouse > diffAfterMouse ? recordAfterMouse : recordBeforeMouse;
                } else if (recordAfterMouse) {
                    d = recordAfterMouse;
                } else {
                    // Last element.
                    d = points[points.length - 1];
                    if (!d) return null;
                }

                return d.position as unknown as TooltipData;
            })
        );
    }
}

export class TooltipHelperTimeGraph extends BisectTooltipHelper {
    getBisectPropertyNameX(): 'x' {
        return 'x';
    }
}

/**
 * Tooltip helper that finds the best tooltip data to be shown
 * for the current mouse location trough Delaunay triangulation.
 */
export class DelaunayTooltipHelper extends BaseTooltipHelper {
    getDataForMouseLocation$(): Observable<TooltipData | null> {
        return combineLatest([this.mouse$, this.positions$]).pipe(
            map(([mouse, positions]) => {
                const points = positionsToPoints(positions);

                const delaunay = Delaunay.from(
                    points,
                    (p: TooltipPoint) => p.x, // X
                    (p: TooltipPoint) => p.y // Y
                );
                const index = delaunay.find(...mouse);

                return points[index]?.position as TooltipData;
            })
        );
    }
}

export class TooltipHelperVeffDayGraph extends DelaunayTooltipHelper {}

export class TooltipHelperFrequencyGraph extends DelaunayTooltipHelper {}
